import * as MobX from "mobx";

import { FieldType, sortByString } from "@shoothill/core";
import { GenericIncludeDeleted, LoginModel, RoleDTO } from "../Models";
import { User, UserDTO } from "Globals/Models/User";
import { action, computed } from "mobx";

import { ApiResult } from "@shoothill/core";
import { AppUrls } from "AppUrls";
import { StoresInstance } from "Globals/Stores/Stores";
import { UserStore } from "Globals/Stores/Domain";
import { ViewModelBase } from "@shoothill/core";

const domainStores = StoresInstance.Domain;

export default class UserViewModel extends ViewModelBase<User> {
    @MobX.observable public userCount: number = 0;
    @MobX.observable private ascOrder = true;
    private userStore: UserStore = StoresInstance.Domain.UserStore;
    @MobX.observable public Valid: boolean = false;
    @MobX.observable public resetLoginAttemptsError = "";
    private adminUsers = MobX.observable<User>([]);

    constructor() {
        super(new User(""));
        this.setDecorators(LoginModel);
        this.getUserRoles();
        this.loadAdminUsersAsync();
    }

    public setUser(user: User, newUser: boolean) {
        this.setValue("id", newUser ? "" : user.id);
        this.setValue("firstName", newUser ? "" : user.firstName);
        this.setValue("lastName", newUser ? "" : user.lastName);
        this.setValue("emailAddress", newUser ? "" : user.emailAddress);
        this.setValue("password", newUser ? "" : user.password);
        this.setValue("role", newUser ? "" : user.role);
    }

    public get(fieldName: any) {
        return this.getValue(fieldName);
    }

    @action
    public set(fieldName: any, value: string | number | boolean | Date) {
        this.setValue(fieldName, value as string);
    }

    public async isFieldValid(fieldName: keyof FieldType<User>, value: any): Promise<boolean> {
        const { isValid, errorMessage } = await this.validateDecorators(fieldName);

        this.setError(fieldName, errorMessage);
        this.setValid(fieldName, isValid);

        return isValid;
    }

    public afterUpdate: undefined;
    public beforeUpdate: undefined;

    @action
    public async loadUsersAsync(): Promise<ApiResult<UserDTO[]>> {
        const includeGenericDeleted: GenericIncludeDeleted = {
            includeDeleted: true,
        };
        const apiResult = await this.Post<UserDTO[]>("/api/user/getall", includeGenericDeleted);

        if (apiResult.wasSuccessful) {
            this.userStore.setUsers(apiResult.payload);
            MobX.runInAction(() => (this.userCount = this.userStore.getUserCount));
        }
        return apiResult;
    }

    @action
    public async loadAdminUsersAsync(): Promise<ApiResult<UserDTO[]>> {
        const apiResult = await this.Post<UserDTO[]>("/api/user/getalladminusers");

        if (apiResult.wasSuccessful) {
            console.log(apiResult.payload);
            let res = apiResult.payload;
            for (let i = 0; i < res.length; i++) {
                const userDTO = res[i];
                const user = new User(userDTO.id);
                user.fromDto(userDTO);
                MobX.runInAction(() => this.adminUsers.push(user));
            }
        }
        return apiResult;
    }

    public async getUserRoles(): Promise<ApiResult<RoleDTO[]>> {
        const apiResult = await this.Post<RoleDTO[]>("/api/user/GetUserRoles", { id: this.getValue("id") });
        if (apiResult.wasSuccessful) {
            apiResult.payload.forEach((role) => {
                MobX.runInAction(() => {
                    const existingRole = domainStores.RoleStore.getRoles.find((r) => r.id === role.id);

                    if (existingRole) {
                        this.model.userRoles.push(existingRole);
                    }
                });
            });
        }
        return apiResult;
    }

    public async resetFailedLoginAttempts(): Promise<void> {
        MobX.runInAction(() => (this.IsLoading = true));
        const apiResult = await this.Post<any>(AppUrls.Server.Admin.ResetFailedLoginAttemptsCount, {
            id: this.getValue("id"),
        });
        if (apiResult.wasSuccessful) {
            MobX.runInAction(() => (this.resetLoginAttemptsError = ""));
        } else {
            MobX.runInAction(() => {
                this.IsErrored = true;
                this.resetLoginAttemptsError = "Unknown Error resetting Failed Login Attempts Count";
            });
        }
        MobX.runInAction(() => (this.IsLoading = false));
    }
    @computed get getAdminUsers(): User[] {
        // let users = this.userStore.getUsers.slice();
        let users = this.adminUsers.slice();
        users.sort((a: UserDTO, b: UserDTO) => {
            if (this.ascOrder) return sortByString(a.firstName, b.firstName);
            else return sortByString(b.firstName, a.firstName);
        });
        return users;
    }

    public getUser = (id: string) => {
        if (id) {
            let user = this.userStore.getUsers.find((u) => u.id === id);
            let admin = this.adminUsers.find((u) => u.id === id);
            return user ? user : admin;
        } else {
            return new User("");
        }
    };

    @computed get getUserCount(): number {
        return this.userCount;
    }

    @computed get getIsLoadingData(): boolean {
        return this.userStore.getIsLoadingData;
    }

    @action
    public setOrderAsc() {
        this.ascOrder = !this.ascOrder;
    }

    @computed get getOrderAsc(): boolean {
        return this.ascOrder;
    }

    public async postUserDetailAsync() {
        const apiResult = await this.Post<UserDTO>(`/api/user/Upsert`, this.getModel);
        if (apiResult.wasSuccessful) {
            let existingUser = this.userStore.getUsers.find((u) => u.id === this.getValue("id"));
            if (existingUser) {
                const index = this.userStore.getUsers.indexOf(existingUser, 0);
                if (index > -1) {
                    this.userStore.getUsers.splice(index, 1);
                }
            }
            apiResult.payload.password = "";
            const newUser = new User(apiResult.payload.id);
            newUser.fromDto(apiResult.payload);
            this.userStore.getUsers.push(newUser);
        }
        return apiResult.payload;
    }
}
